import React, { forwardRef, useId } from 'react';
import styled, { css } from 'styled-components';
import { borders } from '../tokens/borders';
import { animations } from '../tokens/animations';
import { shadows } from '../tokens/shadows';

export type InputVariant = 'default' | 'filled' | 'outlined';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: InputVariant;
  error?: boolean;
  helperText?: string;
  label?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
}

const InputWrapper = styled.div<{ fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label<{ error: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props =>
    props.error ? props.theme.colors.error : props.theme.colors.text};
  margin-bottom: 4px;
`;

const InputContainer = styled.div<{
  variant: InputVariant;
  error: boolean;
  disabled: boolean;
  hasIconLeft: boolean;
  hasIconRight: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  transition: ${animations.transition(
    ['border-color', 'background-color', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeOut
  )};

  ${props => {
    const borderColor = props.error
      ? props.theme.colors.error
      : props.theme.colors.border;
    const focusColor = props.error
      ? props.theme.colors.error
      : props.theme.colors.primary;
    const padY = '12px';
    const padX = '16px';
    const padLeft = props.hasIconLeft ? '44px' : padX;
    const padRight = props.hasIconRight ? '44px' : padX;

    if (props.variant === 'outlined') {
      return css`
        background: ${props.theme.colors.surface};
        border: 2px solid ${borderColor};
        border-radius: ${borders.radius.md};
        padding: ${padY} ${padRight} ${padY} ${padLeft};

        &:focus-within {
          border-color: ${focusColor};
          box-shadow: ${shadows.colored.sm(focusColor)};
          outline: none;
        }
      `;
    }

    if (props.variant === 'filled') {
      return css`
        background: ${props.theme.colors.surfaceHover};
        border: 2px solid transparent;
        border-radius: ${borders.radius.md};
        padding: ${padY} ${padRight} ${padY} ${padLeft};

        &:focus-within {
          border-color: ${focusColor};
          box-shadow: ${shadows.colored.sm(focusColor)};
          outline: none;
        }
      `;
    }

    /* default: underline-only field */
    return css`
      background: transparent;
      border: none;
      border-bottom: 2px solid ${borderColor};
      border-radius: 0;
      padding: ${padY} ${props.hasIconRight ? '32px' : '0'} ${padY}
        ${props.hasIconLeft ? '32px' : '0'};

      &:focus-within {
        border-bottom-color: ${focusColor};
        outline: none;
      }
    `;
  }}

  ${props =>
    props.disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
    `}
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  font-family: ${props =>
    props.theme.typography?.fontFamily?.primary || 'inherit'};
  outline: none;
  width: 100%;
  min-width: 0;

  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const IconLeft = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.textSecondary};
  pointer-events: none;
`;

const IconRight = styled.div`
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const HelperText = styled.span<{ error: boolean }>`
  font-size: 12px;
  color: ${props =>
    props.error ? props.theme.colors.error : props.theme.colors.textSecondary};
  margin-top: -2px;
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'default',
      error = false,
      helperText,
      label,
      iconLeft,
      iconRight,
      fullWidth = true,
      disabled = false,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) => {
    const reactId = useId();
    const inputId = id || `input-${reactId}`;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const describedBy =
      [ariaDescribedBy, helperId].filter(Boolean).join(' ') || undefined;

    return (
      <InputWrapper fullWidth={fullWidth}>
        {label && (
          <Label htmlFor={inputId} error={error}>
            {label}
          </Label>
        )}
        <InputContainer
          variant={variant}
          error={error}
          disabled={disabled}
          hasIconLeft={!!iconLeft}
          hasIconRight={!!iconRight}
        >
          {iconLeft && <IconLeft>{iconLeft}</IconLeft>}
          <StyledInput
            ref={ref}
            id={inputId}
            disabled={disabled}
            aria-invalid={ariaInvalid ?? (error || undefined)}
            aria-describedby={describedBy}
            {...props}
          />
          {iconRight && <IconRight>{iconRight}</IconRight>}
        </InputContainer>
        {helperText && (
          <HelperText id={helperId} error={error}>
            {helperText}
          </HelperText>
        )}
      </InputWrapper>
    );
  }
);

Input.displayName = 'Input';
